import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, errorInfo: ErrorInfo) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
  component?: string; // Nome del componente per logging specifico
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  retryCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null;
  
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorId: '',
    retryCount: 0
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI.
    return { 
      hasError: true, 
      error, 
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { component, onError } = this.props;
    const errorContext = component ? `[ErrorBoundary:${component}]` : '[ErrorBoundary]';
    
    console.error(`${errorContext} Caught an error:`, error);
    console.error(`${errorContext} Component stack:`, errorInfo.componentStack);
    
    this.setState({
      error,
      errorInfo,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Enhanced logging for Advanced RAG system
    this.logAdvancedRAGError(error, errorInfo);
  }
  
  private logAdvancedRAGError = (error: Error, errorInfo: ErrorInfo) => {
    const errorCategory = this.getErrorCategory(error);
    const errorData = {
      component: this.props.component || 'Unknown',
      category: errorCategory,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorId: this.state.errorId,
      retryCount: this.state.retryCount
    };

    console.group(`🚨 Advanced RAG Error - ${errorCategory}`);
    console.error('Error Details:', errorData);
    console.groupEnd();

    // In production, send to monitoring service
    if (process.env.NODE_ENV === 'production') {
      // Example: send to error tracking service
      console.log('[ErrorBoundary] Would send to error tracking:', errorData);
    }
  };
  
  private getErrorCategory = (error: Error): string => {
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';

    if (message.includes('find is not a function') || message.includes('map is not a function')) {
      return 'Data Structure Error';
    }
    if (message.includes('cytoscape') || stack.includes('cytoscape')) {
      return 'Graph Visualization Error';
    }
    if (message.includes('semantic') || message.includes('search')) {
      return 'Semantic Search Error';
    }
    if (message.includes('neo4j') || stack.includes('neo4j')) {
      return 'Graph Database Error';
    }
    if (message.includes('openai') || message.includes('embedding')) {
      return 'AI Processing Error';
    }
    if (message.includes('network') || message.includes('fetch')) {
      return 'Network Error';
    }
    return 'Application Error';
  };
  
  private resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
    
    this.resetTimeoutId = window.setTimeout(() => {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: '',
        retryCount: prevState.retryCount + 1
      }));
    }, 100);
  };

  // CRITICAL FIX: Deep comparison method for resetKeys arrays
  private hasResetKeysChanged(prevKeys: any[] | undefined, currentKeys: any[] | undefined): boolean {
    // If both are undefined/null, no change
    if (!prevKeys && !currentKeys) return false;
    
    // If one is undefined/null and the other isn't, it's a change
    if (!prevKeys || !currentKeys) return true;
    
    // If different lengths, it's a change
    if (prevKeys.length !== currentKeys.length) return true;
    
    // Deep comparison of each element
    return prevKeys.some((key, index) => {
      const currentKey = currentKeys[index];
      
      // For objects/arrays, use JSON comparison (simple but effective)
      if (typeof key === 'object' && typeof currentKey === 'object') {
        return JSON.stringify(key) !== JSON.stringify(currentKey);
      }
      
      // For primitives, direct comparison
      return key !== currentKey;
    });
  }
  
  componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError, retryCount } = this.state;
    
    // CRITICAL FIX: Deep comparison instead of reference comparison for resetKeys
    const resetKeysChanged = this.hasResetKeysChanged(prevProps.resetKeys, resetKeys);
    
    // CONSERVATIVE: Allow only 1 automatic reset to prevent infinite loops
    if (hasError && retryCount === 0 && resetKeysChanged) {
      if (resetOnPropsChange || (resetKeys && resetKeys.some((key, index) => 
        prevProps.resetKeys?.[index] !== key
      ))) {
        console.log(`[ErrorBoundary] Attempting single reset due to props change`);
        this.resetErrorBoundary();
      }
    } else if (hasError && retryCount > 0) {
      console.warn('[ErrorBoundary] Already attempted reset, preventing further automatic resets');
    }
  }

  private getErrorDescription = (category: string): string => {
    switch (category) {
      case 'Data Structure Error':
        return 'A data format issue occurred while processing search results. This usually happens when the API returns unexpected data structures.';
      case 'Graph Visualization Error':
        return 'The graph visualization component encountered an issue. This might be due to large datasets or browser limitations.';
      case 'Semantic Search Error':
        return 'An error occurred during semantic search processing. The AI embedding or similarity calculation failed.';
      case 'Graph Database Error':
        return 'Unable to connect to or query the Neo4j graph database. The system will fall back to simplified results.';
      case 'AI Processing Error':
        return 'OpenAI embedding generation or processing failed. The system may fall back to autonomous search mode.';
      case 'Network Error':
        return 'A network connection issue prevented the request from completing successfully.';
      default:
        return 'An unexpected error occurred in the Advanced RAG system.';
    }
  };
  
  private getRecoveryInstructions = (category: string): string[] => {
    switch (category) {
      case 'Data Structure Error':
        return [
          'Try refreshing the component',
          'Check if search results are loading properly',
          'Try a different search query'
        ];
      case 'Graph Visualization Error':
        return [
          'Try with fewer documents selected',
          'Switch to list view temporarily',
          'Clear browser cache and reload'
        ];
      case 'Semantic Search Error':
        return [
          'Try using autonomous search mode',
          'Check if documents are properly selected',
          'Simplify your search query'
        ];
      case 'Graph Database Error':
        return [
          'Results will show in simplified format',
          'Full graph features will resume automatically',
          'Try again in a few moments'
        ];
      case 'AI Processing Error':
        return [
          'Switch to autonomous search mode',
          'OpenAI services may be temporarily unavailable',
          'Try again with a shorter query'
        ];
      case 'Network Error':
        return [
          'Check your internet connection',
          'Try refreshing the page',
          'Contact support if issue persists'
        ];
      default:
        return [
          'Try refreshing the page',
          'Clear browser cache if needed',
          'Contact support with the error ID'
        ];
    }
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      // If fallback is a function, call it with error and errorInfo
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(
          this.state.error as Error, 
          this.state.errorInfo as ErrorInfo
        );
      }
      
      // If fallback is a ReactNode, return it
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Enhanced fallback UI for Advanced RAG system
      const errorCategory = this.getErrorCategory(this.state.error!);
      const isMinorError = errorCategory === 'Data Structure Error';
      
      return (
        <div className={`${isMinorError ? 'min-h-96' : 'min-h-screen'} flex items-center justify-center bg-red-50 p-4`}>
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full">
            {/* Error Icon and Title */}
            <div className="flex items-center mb-4">
              <div className="bg-red-100 p-3 rounded-full mr-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-red-900">{errorCategory}</h2>
                <p className="text-red-700 text-sm">Advanced RAG System Error</p>
              </div>
            </div>

            {/* Error Message */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 font-medium mb-2">What happened:</p>
              <p className="text-red-700 text-sm">{this.getErrorDescription(errorCategory)}</p>
            </div>

            {/* Recovery Suggestions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-800 font-medium mb-2">Suggested actions:</p>
              <ul className="text-blue-700 text-sm space-y-1">
                {this.getRecoveryInstructions(errorCategory).map((instruction, index) => (
                  <li key={index} className="flex items-start">
                    <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mr-2 mt-2 flex-shrink-0"></span>
                    {instruction}
                  </li>
                ))}
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                onClick={this.resetErrorBoundary}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </button>
              
              <button 
                className="flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </button>
              
              {!isMinorError && (
                <button 
                  className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  onClick={() => window.location.href = '/'}
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </button>
              )}
            </div>

            {/* Debug Information (Development Only) */}
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-4">
                <summary className="cursor-pointer text-gray-600 hover:text-gray-800 flex items-center">
                  <Bug className="w-4 h-4 mr-2" />
                  Debug Information
                </summary>
                <div className="mt-2 p-3 bg-gray-100 rounded text-xs">
                  <p><strong>Error ID:</strong> {this.state.errorId}</p>
                  <p><strong>Component:</strong> {this.props.component || 'Unknown'}</p>
                  <p><strong>Retry Count:</strong> {this.state.retryCount}</p>
                  <pre className="mt-2 text-red-600 overflow-auto">
                    {this.state.error?.toString()}
                  </pre>
                  {this.state.errorInfo && (
                    <pre className="mt-2 overflow-auto">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            {/* Error ID for Support */}
            <p className="text-xs text-gray-500 mt-4 text-center">
              Error ID: <code className="bg-gray-100 px-1 py-0.5 rounded">{this.state.errorId}</code>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };